import React, { useState, useRef } from 'react';
import { Upload, FileText, X, CheckCircle2, AlertCircle, Loader } from 'lucide-react';
import api from '../../services/api';

function ExcelImportModal({ isOpen, onClose, onImportSuccess }) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [preview, setPreview] = useState([]);
  const fileInputRef = useRef();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    setError('');
    setSuccess('');
    setPreview([]);

    if (!selectedFile) return;

    // Validar extensão do arquivo
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    if (!validTypes.includes(selectedFile.type)) {
      setError('Por favor, envie um arquivo Excel (.xlsx, .xls) ou CSV (.csv)');
      return;
    }

    // Validar tamanho (máximo 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('O arquivo não pode exceder 10MB');
      return;
    }

    setFile(selectedFile);

    // Pré-visualizar os primeiros 5 registros
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        // Pega os primeiros 5 registros (cabeçalho + 4 linhas)
        const previewData = jsonData.slice(0, 5);
        setPreview(previewData);
      } catch (err) {
        setError('Erro ao ler o arquivo Excel. Verifique se o formato está correto.');
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/sdr-voz/importar-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setSuccess(`Sucesso! ${response.data.data.total_leads} leads importados. ${response.data.data.duplicados || 0} já existiam.`);
        setFile(null);
        setPreview([]);
        onImportSuccess && onImportSuccess(response.data.data);
        setTimeout(() => {
          onClose();
          setSuccess('');
        }, 2000);
      } else {
        setError(response.data.error || 'Erro ao importar os leads');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Erro desconhecido ao importar';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setPreview([]);
      setError('');
      setSuccess('');
      onClose();
    }
  };

  // Importar XLSX para pré-visualização
  const XLSX = require('xlsx');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">Importar Leads do Excel</h2>
            <button onClick={handleClose} disabled={uploading} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X size={24} />
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Faça upload de um arquivo Excel ou CSV com os leads para importação
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-semibold text-gray-700 mb-2">
              Arraste o arquivo aqui ou clique para selecionar
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Formatos suportados: .xlsx, .xls, .csv (máximo 10MB)
            </p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleChange}
              accept=".xlsx,.xls,.csv"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-secondary transition-colors"
            >
              Selecionar Arquivo
            </button>
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Pré-visualização (primeiros registros)</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  {preview[0].map((header, index) => (
                    <div key={index} className="text-xs font-semibold text-gray-600" style={{ width: '20%', display: 'inline-block' }}>
                      {String(header).trim() || `Coluna ${index + 1}`}
                    </div>
                  ))}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {preview.slice(1).map((row, rowIndex) => (
                    <div key={rowIndex} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                      {row.map((cell, cellIndex) => (
                        <div key={cellIndex} className="text-sm text-gray-700" style={{ width: '20%', display: 'inline-block' }}>
                          {String(cell || '').substring(0, 30)}{String(cell || '').length > 30 ? '...' : ''}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Status Messages */}
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-lg">
              <CheckCircle2 size={16} />
              {success}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleClose}
              disabled={uploading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? (
                <>
                  <Loader size={16} className="animate-spin inline mr-2" />
                  Importando...
                </>
              ) : (
                <>
                  <FileText size={16} className="inline mr-2" />
                  Importar Leads
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExcelImportModal;